import { ZodError } from "zod";
import { ApiError } from "../api/fastapi";
import {
  ArtifactOutcome,
  isArtifactLanded,
  OptroSendJobStatus,
  SendRefusalCode,
  SendRefusalSchema,
} from "../models/audit/optroSend";

// Copy for Send to Optro, keyed on the backend's codes and reasons. Nothing
// the backend says is shown verbatim except `error` and `user_message`,
// which it writes for a user.

export const SIGN_IN_REQUIRED_COPY = "Sign in with Optro to send to Optro.";

const START_REFUSAL_COPY: Record<
  Exclude<SendRefusalCode, "workpaper_checked_out">,
  string
> = {
  send_in_progress: "A send to Optro is already in progress for this session.",
  send_disabled:
    "Send to Optro is not available for this workspace. Ask your Midship admin to enable it.",
  optro_sign_in_required: SIGN_IN_REQUIRED_COPY,
  not_linked:
    "This session is no longer linked to Optro. Refresh the page and try again.",
  session_not_found:
    "This session no longer exists. Refresh the page and try again.",
  optro_upload_not_allowed:
    "You can't add files to this record in Optro. It may be submitted or approved, or your Optro role may not allow editing it.",
};
const NO_PERMISSION_COPY =
  "You don't have permission to send this session to Optro.";
const START_FAILED_COPY = "Couldn't start sending to Optro. Try again.";
// The start request may have reached the backend and queued the send before
// its answer was lost.
export const START_UNCONFIRMED_COPY =
  "Couldn't confirm the send to Optro started. Check when it was last sent before sending again.";
const INDETERMINATE_START_STATUSES = [408, 502, 503, 504];

// The poll's deadline outlasts the send job's own limits, so by then the run
// has ended; the poll just never saw how.
export const SEND_UNCONFIRMED_COPY =
  "Couldn't confirm the send to Optro finished. Check when it was last sent, and send again if needed.";
export const STATUS_UNAVAILABLE_COPY =
  "Couldn't get the status of the send to Optro. Refresh the page in a few minutes to see if it went through.";

const SENT_FIRST_TIME_COPY =
  "Sent to Optro. The workpaper and evidence bundle are on the linked record.";
const SENT_AGAIN_COPY = "Sent an updated version to Optro.";
const COMPLETES_PARTIAL_SEND_COPY =
  "Evidence bundle sent. Optro now has both the workpaper and the evidence bundle.";
// No reason: the backend could not classify the failure, or this build does
// not know the reason it gave.
const EVIDENCE_FAILED_NO_REASON_COPY =
  "Workpaper sent. The evidence bundle failed to send. Try again.";
const JOB_FAILED_COPY = "Sending to Optro failed.";
const NO_REASON_FALLBACK_COPY = "Sending to Optro failed. Try again.";

type ArtifactLabel = "workpaper" | "evidence bundle";

const BYTES_PER_MB = 1024 * 1024;
const megabytes = new Intl.NumberFormat("en", { maximumFractionDigits: 1 });

// "Sep 29, 3:14 PM": re-sends usually happen the same day, so the date alone
// cannot tell two of them apart. A date from another year names it. Null for a
// value that is not a date.
export function formatSendTime(dateString: string): string | null {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  const otherYear = date.getFullYear() !== new Date().getFullYear();
  return new Intl.DateTimeFormat("en", {
    year: otherYear ? "numeric" : undefined,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function checkedOutMessage(
  artifact: ArtifactLabel,
  checkedOutBy: string | null | undefined,
  checkedOutAt: string | null | undefined,
): string {
  const since = checkedOutAt ? formatSendTime(checkedOutAt) : null;
  const sinceClause = since ? ` since ${since}` : "";
  // No holder: Optro doesn't name one for a file open in Office Online, which
  // isn't a check-out anyone can check in.
  if (!checkedOutBy) {
    return `The ${artifact} is checked out or open for editing in Optro by another user${sinceClause}. Send again once they're done.`;
  }
  return `The ${artifact} is checked out in Optro by ${checkedOutBy}${sinceClause}. Ask them to check it in, then send again.`;
}

// checked_out and too_large are about one file, and name it. The rest are
// about the record, the Optro instance or the sign-in, and often fail both
// files at once, so they read the same for either.
function artifactFailureMessage(
  failed: ArtifactOutcome,
  artifact: ArtifactLabel,
): string {
  switch (failed.reason) {
    case "checked_out":
      return checkedOutMessage(
        artifact,
        failed.checked_out_by,
        failed.checked_out_at,
      );
    case "too_large":
      if (failed.size_bytes != null && failed.max_size_bytes != null) {
        // Rounded up, so a file just over the limit never reads as at it.
        const size = Math.ceil((failed.size_bytes / BYTES_PER_MB) * 10) / 10;
        const max = failed.max_size_bytes / BYTES_PER_MB;
        return `The ${artifact} is ${megabytes.format(size)} MB, over Optro's ${megabytes.format(max)} MB limit.`;
      }
      return `The ${artifact} is too large to send to Optro.`;
    case "permission_denied":
      return (
        failed.user_message ||
        "You don't have permission to send to this Optro record. Ask your Optro admin for access."
      );
    case "generation_failed":
      return (
        failed.user_message ||
        "Midship could not generate the file to send. Try again."
      );
    case "reauth_required":
      return "Your Optro sign-in expired. Sign in with Optro, then send again.";
    case "attachable_gone":
      return "This Optro record no longer exists.";
    case "unsupported_storage":
      return "Midship cannot send to this Optro instance's file storage.";
    case "unreachable":
      return "Midship could not reach Optro. Try again.";
    case null:
      return failed.user_message || NO_REASON_FALLBACK_COPY;
  }
}

export interface SendToastCopy {
  variant: "success" | "error";
  message: string;
  description?: string;
}

// The toast for a finished send job. A failed workpaper dominates (nothing
// else matters if the primary deliverable failed), then a failed evidence
// bundle, then the "both landed" sentences. The files are sent independently,
// so when only one failed the description says the other went through: the
// send still counts, and the button now offers a resend.
export function describeSendJob(
  job: OptroSendJobStatus,
  completesPartialSend: boolean,
): SendToastCopy {
  if (job.status !== "success" || !job.outcome) {
    return job.error
      ? { variant: "error", message: JOB_FAILED_COPY, description: job.error }
      : { variant: "error", message: NO_REASON_FALLBACK_COPY };
  }

  const { workpaper, evidence } = job.outcome;
  if (workpaper.status === "failed") {
    return {
      variant: "error",
      message: artifactFailureMessage(workpaper, "workpaper"),
      description: isArtifactLanded(evidence)
        ? "The evidence bundle was sent."
        : undefined,
    };
  }
  if (evidence.status === "failed") {
    return evidence.reason
      ? {
          variant: "error",
          message: artifactFailureMessage(evidence, "evidence bundle"),
          description: "The workpaper was sent.",
        }
      : { variant: "error", message: EVIDENCE_FAILED_NO_REASON_COPY };
  }

  const bothFirstTime =
    workpaper.status === "created" && evidence.status === "created";
  let message = SENT_AGAIN_COPY;
  if (completesPartialSend) {
    message = COMPLETES_PARTIAL_SEND_COPY;
  } else if (bothFirstTime) {
    message = SENT_FIRST_TIME_COPY;
  }
  return { variant: "success", message };
}

// The work-step routes' own flag check (WORK_STEPS_DISABLED_DETAIL in the
// backend's work_steps/dependencies.py). It answers 403 before the send's
// guards run, so it carries a plain string rather than a refusal code.
const WORK_STEPS_DISABLED_DETAIL =
  "Work Steps is not enabled for this workspace";

// The send routes' own session lookups, made before the guards run. Any other
// 404 (a route this backend doesn't have yet) isn't about the session.
const SESSION_NOT_FOUND_DETAILS = new Set([
  "Session not found",
  "Work step session not found",
]);

// Whether the start request may have queued a send though no usable answer
// came back: none at all, or a success this build can't read.
export function isStartUnconfirmed(error: unknown): boolean {
  if (error instanceof ZodError) return true;
  return (
    error instanceof ApiError &&
    (error.isNetworkError ||
      INDETERMINATE_START_STATUSES.includes(error.status))
  );
}

// Why the start endpoint refused, from its structured code. Without one, a
// 403 is the work-steps flag check or the Midship permission check, and a 404
// the router's own lookup of the session, made before the guards run.
export function startRefusalMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return START_FAILED_COPY;
  const refusal = SendRefusalSchema.safeParse(error.detailData);
  if (refusal.success) {
    const { code, checked_out_by, checked_out_at } = refusal.data;
    if (code === "workpaper_checked_out") {
      return checkedOutMessage("workpaper", checked_out_by, checked_out_at);
    }
    return START_REFUSAL_COPY[code];
  }
  if (error.status === 403 && error.detail === WORK_STEPS_DISABLED_DETAIL) {
    return START_REFUSAL_COPY.send_disabled;
  }
  if (error.status === 403) return NO_PERMISSION_COPY;
  if (
    error.status === 404 &&
    SESSION_NOT_FOUND_DETAILS.has(error.detail ?? "")
  ) {
    return START_REFUSAL_COPY.session_not_found;
  }
  return START_FAILED_COPY;
}
